import { ComponentType, lazy, LazyExoticComponent } from "react"
import { LoadPageErrorHandler } from "ui/loadPageErrorHandler"

interface RetryOptions {
  retries?: number
  retryDelay?: number
}

const CHUNK_ERROR_PATTERNS = [
  "Failed to fetch",
  "dynamically imported module",
  "Loading chunk",
  "Importing a module script failed",
  "is not a valid JavaScript MIME type"
]

const RELOAD_GUARD_KEY = "safeLazy:reload-attempted"

const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false
  return CHUNK_ERROR_PATTERNS.some((pattern) => error.message.includes(pattern))
}

/**
 * Wraps lazy import with error handling and retry logic
 */
export function safeLazy<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options: RetryOptions = {}
): LazyExoticComponent<T> {
  const { retries = 3, retryDelay = 1000 } = options

  const loadWithRetry = async (attempt = 1): Promise<{ default: T } | any> => {
    try {
      const result = await importFn()
      sessionStorage.removeItem(RELOAD_GUARD_KEY)
      return result
    } catch (error) {
      if (isChunkLoadError(error)) {
        if (attempt <= retries) {
          console.warn(
            `Chunk loading failed, retrying (${attempt}/${retries})...`
          )
          await new Promise((resolve) => setTimeout(resolve, retryDelay))
          return loadWithRetry(attempt + 1)
        }

        // Stale chunk hashes after deploy: reload once to fetch fresh index.html
        if (!sessionStorage.getItem(RELOAD_GUARD_KEY)) {
          sessionStorage.setItem(RELOAD_GUARD_KEY, "1")
          window.location.reload()
          return new Promise(() => {})
        }
      }

      return { default: () => <LoadPageErrorHandler /> }
    }
  }

  return lazy(() => loadWithRetry())
}
